import { PUBLIC_COMETH_API_KEY, PUBLIC_CONNECT_4337_COMETH_API_KEY } from '$env/static/public';
import { publicClient } from '$lib/utils/publicClient';
import { PASSKEY_NAME } from '$lib/constants/passkeys';
import { gnosis } from 'viem/chains';
import {
  createComethPaymasterClient,
  createSafeSmartAccount,
  createSmartAccountClient,
  ENTRYPOINT_ADDRESS_V07,
  type ComethSafeSmartAccount,
  type ComethSmartAccountClient,
} from '@cometh/connect-sdk-4337';
import { http, type HDAccount, type PrivateKeyAccount, type Transport } from 'viem';
import type { Address } from '$types/address';

export const cometh4337SignerConfig = {
  webAuthnOptions: {
    authenticatorSelection: {
      authenticatorAttachment: 'platform',
      residentKey: 'preferred',
      userVerification: 'preferred',
    },
  },
  passKeyName: PASSKEY_NAME,
  disableEoaFallback: true,
};

export const cometh4337Params = {
  apiKeyLegacy: PUBLIC_COMETH_API_KEY,
  apiKey4337: PUBLIC_CONNECT_4337_COMETH_API_KEY,
  publicClient: publicClient,
  chain: gnosis,
  CHAIN_ID: '100',
  bundlerUrl: `https://bundler.cometh.io/100?apikey=${PUBLIC_CONNECT_4337_COMETH_API_KEY}`,
  paymasterUrl: `https://paymaster.cometh.io/100?apikey=${PUBLIC_CONNECT_4337_COMETH_API_KEY}`,
  paymasterParams: {
    paymaster: '0x6a6B7f6012ee5bEF1cDf95df25e5045C7727c739',
    dummySignature:
      '0x5a7196743347ddbbd2e151806feebc9fb19ef1a118733923f7e99f8f548cd8a15eeb3804cc7a9078e3601c7a15d75fe98e62a1e09de2188edef7d5145a9be5cf1b',
    validUntil: 100 * 60 * 60,
    validAfter: '0x0000000000001234',
  },
};

type Cometh4337Wallet = {
  smartAccountClient: ComethSmartAccountClient<
    Transport,
    typeof cometh4337Params.chain,
    ComethSafeSmartAccount,
    typeof publicClient
  >;
  smartAccount: ComethSafeSmartAccount;
  paymasterClient: ReturnType<typeof createComethPaymasterClient>;
  walletAddress: Address;
};

const getCometh4337WalletCache: Record<Address, Cometh4337Wallet> = {};

export const getCometh4337Wallet = async (
  address?: Address,
  signer?: PrivateKeyAccount | HDAccount,
): Promise<Cometh4337Wallet> => {
  if (address !== undefined && getCometh4337WalletCache[address]) {
    return getCometh4337WalletCache[address];
  }

  const { apiKey4337, chain, bundlerUrl, paymasterUrl } = cometh4337Params;

  const smartAccount = await createSafeSmartAccount({
    apiKey: apiKey4337,
    chain: chain,
    entryPoint: ENTRYPOINT_ADDRESS_V07,
    smartAccountAddress: address,
    comethSignerConfig: cometh4337SignerConfig,
    clientTimeout: 180_000,
    signer: signer ?? undefined,
    publicClient: publicClient,
  });

  const paymasterClient = createComethPaymasterClient({
    transport: http(paymasterUrl),
    chain: chain,
    publicClient: publicClient,
  });

  const smartAccountClient = createSmartAccountClient({
    account: smartAccount,
    client: publicClient,
    chain: chain,
    paymaster: paymasterClient,
    bundlerTransport: http(bundlerUrl, { retryCount: 10, retryDelay: 200, timeout: 180_000 }),
    userOperation: {
      estimateFeesPerGas: async () => {
        return await paymasterClient.getUserOperationGasPrice();
      },
    },
  });

  const response = {
    smartAccountClient,
    smartAccount,
    paymasterClient,
    walletAddress: smartAccount.address ?? '',
  };

  if (address !== undefined) {
    getCometh4337WalletCache[address] = response;
  }

  return response;
};
